import { redirect } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  updateEmail,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  getAuth,
  sendEmailVerification,
} from "firebase/auth";
import { setDoc, doc, updateDoc, getDoc } from "firebase/firestore";
import { firestore, auth } from "@redux/firebase";
import { toast } from "react-toastify";
import i18next from "i18next";
import processFile from "./textExtractor";
import { getQuestion } from "@lib/firestoreQueries";
import { getFeedback, processMultipleImages } from "@services/openaiApi";
import { getUser } from "@lib/firestoreQueries";
import { useCallback } from "react";
import { getRemoteConfigValue } from "../../redux/actions/remoteConfigActions";
import { getLastVisitedPage, getDefaultAuthenticatedPage, prendiDopoAccesso } from "../../utils/lastPageTracker";
import { trackEvent, trackPasswordChanged, trackUploadFailed, setAcquisitionUserProperties } from "../../utils/analytics";
import { getStoredAttribution } from "../../utils/attribution";
import { findCachedDocument } from "../../utils/documentCache";
import { markReturnVisit } from "@lib/returnVisit";
import { riattivaSeDisattivato } from "@lib/disattivazione";
import { createGamificationProfile } from "@services/gamificationService";
import { QuestionsNotFoundError } from "../../utils/appErrors";
import { sendErrorToSentry } from "../../utils/errorThrottle";
import { validateFeedbackResponse } from "../../utils/responseValidators";
import { initPixelWithUserData } from "@utils/facebookPixel";
import { resolveVaultPathsByFile } from "./vaultPathsResolver";
import { store } from "@redux/store";
import { setExtractionProgress, clearExtractionProgress } from "../../redux/actions/docUploadProgressAction";
import * as cronometro from "@services/cronometroFasi";
import { motivoDaErrore, chiaveAvviso } from "../../utils/erroreEstrazione";
import { eHeic, eErroreHeic } from "@services/fotoHeic";

// Helper per accedere alle traduzioni nei file non-React
const t = (key, options) => i18next.t(key, { ns: 'common', ...options });

/**
 * L'avviso di un file che non si e' letto (STU-424).
 *
 * Parte da qui, dall'azione, e non da un effetto della card: l'azione del
 * router continua a girare anche quando lo studente ha lasciato la Home. Il
 * 23/09 la lettura di un video e' finita dopo che la studentessa se n'era
 * andata, e nessuno gliel'ha detto. Il ToastContainer sta alla radice
 * (getApp.js), sopra il router: l'avviso compare su qualunque pagina.
 *
 * Uno per file: il toastId e' il file, e processFile non ne manda piu' uno
 * suo per lo stesso fallimento.
 */
const avvisaLetturaFallita = (file, motivo) => {
  try {
    toast.error(
      i18next.t(chiaveAvviso(motivo), { ns: 'upload', fileName: file?.name || '' }),
      { toastId: `lettura-fallita:${file?.name || ''}|${file?.size ?? ''}`, autoClose: 20000 }
    );
  } catch (_) {
    // un avviso che non parte non deve fermare la lettura degli altri file
  }
};

/**
 * L'elemento della coda per un file che non si e' letto: resta nella card
 * come errore, con il nome, il motivo e la X per toglierlo, e Genera non lo
 * manda. `estrazioneFallita` e `motivoErrore` si mettono QUI, dove l'elemento
 * nasce: e' l'accordo con chi lavora sul reducer (STU-424).
 */
const elementoFallito = (file, errore, motivo) => ({
  name: file.name,
  type: file.type,
  size: file.size,
  fileSize: file.size,
  extractedText: '',
  errorMessage: errore?.errorMessage || errore?.message || 'Errore sconosciuto',
  estrazioneFallita: true,
  motivoErrore: motivo,
});

// login action
export const loginAction = async ({ request }) => {
  const formData = await request.formData();
  const email = formData.get("email").trim();
  const password = formData.get("password").trim();

  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    if (!user || !user.emailVerified) {
      throw new Error("Email not verified. Please verify your email.");
    }

    // Ottieni i dati dell'utente da Firestore
    const userDoc = await getDoc(doc(firestore, "students", user.uid));
    if (!userDoc.exists()) {
      throw new Error("User data not found");
    }

    const userData = userDoc.data();

    // Funzione per verificare l'inattività dell'utente
    const checkUserInactivity = (lastLoginAt) => {
      if (!lastLoginAt) return false;
      
      const lastLogin = lastLoginAt instanceof Date ? lastLoginAt : lastLoginAt.toDate();
      const currentDate = new Date();
      const twoWeeksInMs = 14 * 24 * 60 * 60 * 1000; // 14 giorni in millisecondi
      
      const timeDifference = currentDate.getTime() - lastLogin.getTime();
      
      return timeDifference > twoWeeksInMs;
    };

    const isInactive = checkUserInactivity(userData.lastLoginAt);

    // Mette da parte l'assenza PRIMA che il lastLoginAt qui sotto la cancelli:
    // e' il segnale su cui TrialOfferModal decide la traccia del rientro.
    markReturnVisit(user.uid, userData.lastLoginAt);
    // Chi aveva disattivato l'account e rientra lo riaccende (STU-462).
    riattivaSeDisattivato(user.uid, userData);

    if(isInactive) {
      await updateDoc(doc(firestore, "students", user.uid), {
        currentStep: 0,
      });
    }

    if (isInactive) {
      trackEvent('login_returning_user', {
        category: 'authentication',
        inactive_period_days: Math.floor((new Date().getTime() - userData.lastLoginAt.toDate().getTime()) / (24 * 60 * 60 * 1000))
        // user_id will be auto-injected
      });
    }

    // Aggiorna lastLoginAt
    await setDoc(doc(firestore, "students", user.uid), {
      ...userData,
      lastLoginAt: new Date()
    }, { merge: true });

    // Track successful login
    trackEvent('login_success', {
      category: 'authentication',
      method: 'email',
      is_returning_user: !isInactive
      // user_id will be auto-injected
    });

    // Salva l'ID utente nel localStorage
    localStorage.setItem("userId", JSON.stringify(user.uid));

    // Inizializza Meta Pixel Advanced Matching con dati utente
    initPixelWithUserData({ ...userData, uid: user.uid });

    toast.success(t('toast.success.loginSuccess'));

    // Determina la pagina di destinazione: il link da cui si era arrivati senza sessione
    // (STU-463), poi l'ultima pagina visitata, poi la home
    const lastPage = getLastVisitedPage(true);
    const redirectTo = prendiDopoAccesso() || lastPage || getDefaultAuthenticatedPage();

    // Ritorna i dati dell'utente e il flag di successo
    // 🎮 GAMIFICATION: Mostra DailyReturnModal solo per utenti che ritornano (non nuovi)
    // - newUser === true → utente appena registrato, vedrà OnboardingModal (non DailyReturn)
    // - newUser === false → ha completato onboarding, è un returning user
    // - newUser === undefined → utente legacy pre-flag, è un returning user
    const isReturningUser = userData.newUser !== true;

    return {
      isSuccessful: true,
      userData: { ...userData, uid: user.uid },
      redirectTo: redirectTo,
      showDailyReturnModal: isReturningUser
    };
  } catch (error) {
    console.error("Login error:", error);
    let errorMessage = "Si è verificato un errore durante il login.";
    let errorType = 'unknown';

    if (error.code === 'auth/wrong-password') {
      errorMessage = "Password non corretta";
      errorType = 'wrong_password';
    } else if (error.code === 'auth/user-not-found') {
      errorMessage = "Utente non trovato";
      errorType = 'user_not_found';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = "Troppi tentativi. Riprova più tardi";
      errorType = 'too_many_requests';
    } else if (error.message) {
      errorMessage = error.message;
      errorType = error.code || 'unknown';
    }

    // Track login failure
    try {
      trackEvent('login_failed', {
        category: 'authentication',
        error_type: errorType,
        login_method: 'email',
        error_message: errorMessage
      });
    } catch (trackingError) {
      console.error('[Analytics] Error tracking login_failed:', trackingError);
    }

    toast.error(errorMessage);
    return {
      isSuccessful: false,
      message: errorMessage
    };
  }
};

// sign up action
export const signUpAction = async ({ request }) => {
  let formData;
  try {
    formData = await request.formData();
    // 🌐 Salva la lingua corrente del browser per tracciamento
    const currentLanguage = i18next.language || localStorage.getItem('i18nextLng') || 'it';
    
    const user = {
      fullName: formData.get("fullName").trim(),
      email: formData.get("email").trim(),
      emailUseConsent: formData.get("emailUseConsent").trim(),
      studyAt: formData.get("study"),
      documentsUploaded: 0,
      maxDocumentsUpload: 2,
      examsAttempted: 0,
      flashcardsFlipped: 0,
      multipleChoiceAnswered: 0,
      plan: "Base",
      policyAccepted: formData.get("policyAccepted"),
      ageRange: formData.get("ageRange"),
      amountEarned: 0,
      friendsInvited: 0,
      createdAt: new Date(),
      newUser: true, // Aggiungo la prop newUser nel database
      preferredLanguage: currentLanguage, // 🌐 Salva lingua per tracciamento
    };
    const userCredential = await createUserWithEmailAndPassword(auth, user.email, formData.get("password").trim());
    await setDoc(doc(firestore, "students", userCredential.user.uid), user, { merge: true });
    await updateProfile(userCredential.user, { displayName: user.fullName });

    // Persiste first-touch attribution sul doc utente (idempotente, non blocca il signup)
    try {
      const attribution = getStoredAttribution();
      const acquisitionData = attribution
        ? { source: attribution.source, medium: attribution.medium, campaign: attribution.campaign, content: attribution.content, landed_at: attribution.landed_at, signup_at: new Date().toISOString() }
        : { source: 'direct', medium: 'none', campaign: null, content: null, landed_at: null, signup_at: new Date().toISOString() };
      await setDoc(doc(firestore, "students", userCredential.user.uid), { acquisition: acquisitionData }, { merge: true });
      setAcquisitionUserProperties(acquisitionData);
    } catch (attributionError) {
      console.warn('[Attribution] Persistence failed (non-blocking):', attributionError);
    }

    // 🎮 GAMIFICATION: Crea il profilo gamification per il nuovo utente
    try {
      await createGamificationProfile(userCredential.user.uid, user.fullName);
      console.log('🎮 Gamification profile created for new user:', userCredential.user.uid);
    } catch (gamificationError) {
      console.error('🎮 Error creating gamification profile:', gamificationError);
      // Non bloccare la registrazione per errori di gamification
    }
    
    // Inizializza Meta Pixel Advanced Matching con dati utente
    initPixelWithUserData({
      email: user.email,
      fullName: user.fullName,
      uid: userCredential.user.uid,
    });

    // Invio email di benvenuto
    // La mail di benvenuto (14) la manda il server alla nascita della scheda students
    // (functions/src/benvenuto.js, STU-472), per ogni strada d'iscrizione.

    const signUpVersion = await getRemoteConfigValue("UploadDoc_with_preview");

    // Invia automaticamente l'email di verifica
    await userCredential.user.sendEmailVerification();

    // Segna che l'email è stata inviata per evitare doppio invio nella pagina di verifica
    localStorage.setItem('emailVerificationSent', 'true');
    localStorage.setItem('emailVerificationTime', Date.now().toString());

    // Track email verification sent
    const emailDomain = user.email.split('@')[1] || 'unknown';
    trackEvent('email_verification_sent', {
      category: 'authentication',
      user_email_domain: emailDomain
      // user_id will be auto-injected
    });

    // Track signup completion
    try {
      trackEvent('signup_completed', {
        category: 'authentication',
        method: 'email',
        version: signUpVersion || 'B'
        // user_id will be auto-injected by trackEvent
      });

      // Goal DataFast: vedi il commento gemello in useSignInGoogle. Il contatore delle
      // registrazioni era finito a zero perche' viveva solo dentro EmailConsents.
      window?.datafast?.('signup_completed', { method: 'email' });
      window?.datafast?.('identify', { user_id: userCredential.user.email || userCredential.user.uid });
    } catch (trackingError) {
      console.error("Errore durante il tracciamento:", trackingError);
    }

    // Reindirizza direttamente alla pagina di controlla-verifica-email
    return redirect("/controlla-verifica-email");
  } catch (error) {
    // Track signup error
    trackEvent('signup_error', {
      category: 'authentication',
      error_type: 'signup',
      error_code: error.code || 'unknown',
      error_message: error.message || 'Unknown error'
      // user_id will be 'anonymous' since signup failed
    });

    if (error.code === "auth/email-already-in-use") return { message: "Email già in uso" };
    return { message: error.message };
  }
};

// reset password action
export const resetPasswordAction = async ({ request }) => {
  try {
    const formData = await request.formData();
    const email = formData.get("email");

    // check email format
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regex.test(email)) throw new Error("Inserisci un corretto formato");

    await sendPasswordResetEmail(auth, email);

    // Track password reset request
    trackPasswordChanged();

    toast.success(t('toast.success.passwordResetSent'));
    return redirect("/");
  } catch (error) {
    // check if email is not valid
    if (error.message === "Inserisci un corretto formato") return { message: error.message };

    // send generic error message
    toast.error(error.message);
    return redirect("/reimposta-password");
  }
};

// check verify email action
export const checkVerifyEmailAction = async ({ request }) => {
  try {
    const formData = await request.formData();
    const emailVerified = formData.get("emailVerified");

    // Se il frontend ha già verificato, salta il doppio check
    const user = getAuth().currentUser;

    if (!emailVerified) {
      // Solo se non abbiamo verificato nel frontend, facciamo reload e check
      await user.reload();

      if (!user.emailVerified) {
        return { error: true, message: "Email non ancora verificata" };
      }
    }

    // retrieve user data
    const userInfo = await getUser(user.uid);
    localStorage.setItem("userId", JSON.stringify(user.uid));

    return { user: userInfo };
  } catch (error) {
    console.error("Errore in checkVerifyEmailAction:", error);
    toast.error(error.message);
    return { error: true, message: error.message };
  }
};

// upload notes action
export const uploadNotesAction = async ({ request }) => {
  const formData = await request.formData();
  const files = formData.getAll("notes");

  // Correlazione per INDICE calcolata PRIMA dello split standalone
  // images/allOtherFiles qui sotto: le referenze ai File restano le stesse
  // per tutta l'azione, quindi una Map file -> path funziona anche dopo lo
  // split (mai il nome file come chiave, due sottocartelle diverse possono
  // avere file omonimi).
  const vaultPathsByFile = resolveVaultPathsByFile(files, formData.get('vaultPaths'));
  // Provenienza (Drive, GitHub): `request.formData()` RICREA i File, quindi
  // l'espando `origin` messo dal connettore non arriva qui (misurato 26/08).
  // useUploadFiles la manda come JSON per indice nel campo 'origins', stesso
  // meccanismo di vaultPaths.
  const originsByFile = resolveVaultPathsByFile(files, formData.get('origins'));

  let promiseArray = [];

  // 🚀 OTTIMIZZAZIONE INTELLIGENTE: Separa SOLO immagini caricate direttamente dall'utente
  const standaloneImageFiles = [];
  const allOtherFiles = [];
  
  for (const file of files) {
    // 🎯 LOGICA INTELLIGENTE:
    // ✅ Sistema batch OCR → SOLO per immagini caricate direttamente (JPG, PNG, ecc.)
    // ✅ Sistema normale → PDF, Word, PowerPoint, ecc. (anche se contengono immagini)
    // 
    // Questo evita che PDF con immagini vengano processati dal sistema batch OCR,
    // mantenendo il comportamento normale per tutti i documenti strutturati.
    
    if (file.type && file.type.startsWith('image/') && 
        !file.name.toLowerCase().includes('extracted') && // Esclude immagini estratte da PDF
        !file.metadata?.extractedFromDocument) { // Esclude immagini con metadata di estrazione
      standaloneImageFiles.push(file);
    } else {
      allOtherFiles.push(file);
    }
  }
  
  // 🎯 Processa SOLO le immagini standalone con il sistema batch ottimizzato
  if (standaloneImageFiles.length > 0) {
    // 📦 CACHE: Verifica cache anche per immagini singole
    const imageUserID = localStorage.getItem("userId")?.replace(/"/g, '');
    
    // Per immagine singola, verifica prima la cache
    if (standaloneImageFiles.length === 1 && imageUserID) {
      const singleImage = standaloneImageFiles[0];
      console.log(`🔍 Cache lookup per immagine: "${singleImage.name}" (${singleImage.size} bytes)`);
      
      const cachedImage = await findCachedDocument(imageUserID, singleImage.name, singleImage.type, singleImage.size);
      
      if (cachedImage) {
        console.log(`📦 Cache HIT per immagine "${singleImage.name}" - uso testo cached`);
        const processedFromCache = {
          name: singleImage.name,
          type: singleImage.type,
          fileize: singleImage.size,
          extractedText: cachedImage.text,
          fromCache: true,
          cachedDocumentId: cachedImage.documentId,
          metadata: {
            processedCount: 1,
            processedAt: new Date().toISOString(),
            method: 'cache'
          }
        };
        promiseArray.push(processedFromCache);
        const chiaveCache = cronometro.apriGiro(processedFromCache, { strada: cronometro.STRADE.CACHE });
        cronometro.testoPronto(chiaveCache, cachedImage.text?.length || 0);
        // Rimuovi l'immagine dalla lista (già in cache)
        standaloneImageFiles.length = 0;
      } else {
        console.log(`📭 Cache MISS per immagine "${singleImage.name}" - procedo con OCR`);
      }
    }
    
    // Se la cache ha già gestito tutte le immagini, salta l'elaborazione batch
    if (standaloneImageFiles.length === 0) {
      console.log(`✅ Tutte le immagini servite da cache, skip elaborazione batch`);
    } else {
      console.log(`🚀 Elaborazione batch ottimizzata di ${standaloneImageFiles.length} immagini standalone via backend...`);

    // Mostra toast iniziale per grandi batch
    if (standaloneImageFiles.length > 10) {
      toast.info(t('toast.info.imagesProcessing', { count: standaloneImageFiles.length }), {
        autoClose: false,
        toastId: 'batch-processing'
      });
    }

    // Le foto scattate insieme diventano UN documento (vedi `processedImages`
    // qui sotto): il cronometro segue quell'identita', col nome che avra' e
    // la dimensione della prima foto, gli stessi che finiscono in Redux.
    const nomeBatch = standaloneImageFiles.length > 1
      ? t('documents.photoBatchName', { count: standaloneImageFiles.length })
      : standaloneImageFiles[0].name;
    const chiaveBatch = cronometro.apriGiro(
      { name: nomeBatch, type: standaloneImageFiles.length > 1 ? 'image/multiple' : standaloneImageFiles[0].type, size: standaloneImageFiles[0].size },
      { strada: cronometro.STRADE.MULTIPART }
    );
    cronometro.iniziaFase(chiaveBatch, cronometro.FASI.ESTRAZIONE);

    try {
      // Opzioni per il tracking del progresso
      const progressOptions = {
        onProgress: (progressData) => {
          console.log(`📊 Progresso: ${progressData.phase} - ${progressData.progress}% - ${progressData.message}`);
          try {
            store.dispatch(setExtractionProgress({
              fileName: nomeBatch,
              phase: progressData?.phase || null,
              progress: progressData?.progress,
              message: progressData?.message,
            }));
          } catch (_) {
            // un progresso che non arriva non deve fermare l'OCR
          }
        },
        onStatusUpdate: (status) => {
          console.log(`📋 Stato: ${status.status} - ${status.message}`);

          // Aggiorna il toast con lo stato corrente
          if (standaloneImageFiles.length > 10) {
            if (status.status === 'completed') {
              toast.update('batch-processing', {
                render: status.message,
                type: 'success',
                autoClose: 3000
              });
            } else if (status.status === 'fallback') {
              toast.update('batch-processing', {
                render: status.message,
                type: 'warning',
                autoClose: false
              });
            } else {
              toast.update('batch-processing', {
                render: status.message,
                type: 'info',
                autoClose: false
              });
            }
          }
        }
      };

      // Usa il nuovo sistema di elaborazione batch ottimizzato
      const combinedText = await processMultipleImages(standaloneImageFiles, progressOptions);

      // Crea un oggetto processedFile per il testo combinato
      const processedImages = {
        /* Il nome diventa il TITOLO del documento (lib/file.js): "5 immagini
           elaborate" e' il log della macchina, non un titolo che lo studente
           ritrova nella libreria. */
        name: nomeBatch,
        type: standaloneImageFiles.length > 1 ? 'image/multiple' : standaloneImageFiles[0].type,
        size: standaloneImageFiles[0].size, // Aggiungi size per cache future
        extractedText: combinedText,
        files: standaloneImageFiles,
        metadata: {
          processedCount: standaloneImageFiles.length,
          processedAt: new Date().toISOString(),
          method: 'batch_optimized'
        }
      };

      promiseArray.push(processedImages);
      cronometro.testoPronto(chiaveBatch, combinedText?.length || 0);
      console.log(`✅ ${standaloneImageFiles.length} immagini standalone elaborate con successo tramite backend batch!`);

      // Mostra toast di successo finale
      if (standaloneImageFiles.length <= 10) {
        toast.success(t('toast.success.imagesProcessed', { count: standaloneImageFiles.length }));
      }

    } catch (error) {
      console.error(`Errore nell'elaborazione batch delle immagini:`, error);
      cronometro.segnaFallimento(chiaveBatch, { fase: cronometro.FASI.ESTRAZIONE, errore: error?.message || 'Batch processing failed' });

      // Foto HEIC che nemmeno la conversione apre: riprovarle una per una
      // fallirebbe uguale, e «riprova piu' tardi» sarebbe falso (STU-450).
      // Si riprovano solo le altre foto e si dice cosa fare con queste.
      const heicIlleggibili = eErroreHeic(error);

      // Track upload fallito per batch immagini
      try {
        for (const imageFile of standaloneImageFiles) {
          const fileExtension = imageFile.name ? '.' + imageFile.name.split('.').pop().toLowerCase() : '.unknown';
          trackUploadFailed({
            error_code: heicIlleggibili ? 'HEIC_NOT_DECODABLE' : 'BATCH_PROCESSING_ERROR',
            error_message: (error.message || 'Batch processing failed').substring(0, 200),
            file_extension: fileExtension,
            mime_type: imageFile.type || 'image/unknown',
            file_size_mb: imageFile.size ? parseFloat((imageFile.size / (1024 * 1024)).toFixed(2)) : 0,
            upload_source: 'file_picker',
            extraction_method: 'batch_ocr'
          });
        }
      } catch (trackingError) {
        console.error("Errore nel tracciamento batch upload fallito:", trackingError);
      }

      // Chiudi il toast di elaborazione se presente
      toast.dismiss('batch-processing');

      // Mostra errore all'utente
      if (heicIlleggibili) {
        toast.error(t('toast.warning.heicNotReadable'), { autoClose: 12000 });
      } else {
        toast.error(t('toast.warning.imagesFallbackProcessing'));
      }

      // Fallback: usa il processamento singolo legacy solo come ultima risorsa
      console.warn("⚠️ Fallback al processamento singolo legacy...");
      let fallbackSuccess = 0;
      const daRiprovare = heicIlleggibili
        ? standaloneImageFiles.filter((imageFile) => !eHeic(imageFile))
        : standaloneImageFiles;

      for (const imageFile of daRiprovare) {
        // Ogni foto del ripiego e' un documento a se', col suo giro.
        const chiaveFoto = cronometro.apriGiro(imageFile, { strada: cronometro.STRADE.MULTIPART });
        cronometro.iniziaFase(chiaveFoto, cronometro.FASI.ESTRAZIONE);
        try {
          const processedFile = await processFile(imageFile);
          if (processedFile) {
            // La dimensione serve al cronometro per ritrovare il file dopo
            // Redux, come gia' fa il percorso normale piu' sotto.
            processedFile.size = imageFile.size;
            processedFile.fileSize = imageFile.size;
            cronometro.testoPronto(chiaveFoto, processedFile.extractedText?.length || 0);
            promiseArray.push(processedFile);
            fallbackSuccess++;
          }
        } catch (singleError) {
          console.error(`Errore processando ${imageFile.name}:`, singleError);
          cronometro.segnaFallimento(chiaveFoto, { fase: cronometro.FASI.ESTRAZIONE, errore: singleError?.message || singleError?.errorMessage || 'Single image fallback failed' });
          // Track anche gli errori singoli nel fallback
          try {
            const fileExtension = imageFile.name ? '.' + imageFile.name.split('.').pop().toLowerCase() : '.unknown';
            trackUploadFailed({
              error_code: 'FALLBACK_PROCESSING_ERROR',
              error_message: (singleError.message || 'Single image fallback failed').substring(0, 200),
              file_extension: fileExtension,
              mime_type: imageFile.type || 'image/unknown',
              file_size_mb: imageFile.size ? parseFloat((imageFile.size / (1024 * 1024)).toFixed(2)) : 0,
              upload_source: 'file_picker',
              extraction_method: 'single_fallback'
            });
          } catch (trackErr) {
            console.error("Errore nel tracciamento fallback:", trackErr);
          }
        }
      }

      if (fallbackSuccess > 0) {
        toast.warning(t('toast.warning.imagesPartialSuccess', { success: fallbackSuccess, total: standaloneImageFiles.length }));
      } else if (!heicIlleggibili) {
        toast.error(t('toast.warning.imagesProcessingFailed'));
      }
    }
    } // Chiusura else (standaloneImageFiles.length > 0 dopo cache check)
  }
  
  // Processa tutti gli altri file con il sistema normale (PDF, Word, PowerPoint, ecc.)
  // 📦 CACHE: Verifica se il documento è già stato processato in precedenza
  const userID = localStorage.getItem("userId")?.replace(/"/g, '');
  console.log(`📦 CACHE: userID=${userID}, files da processare=${allOtherFiles.length}`);
  
  for (const file of allOtherFiles) {
    console.log(`📦 CACHE: Processando file "${file.name}", size=${file.size}, type=${file.type}`);
    // Il cronometro parte qui, non nel picker: fra la scelta del file e
    // l'arrivo in questa action c'e' solo il giro di fetcher.submit.
    const chiaveGiro = cronometro.apriGiro(file);
    cronometro.iniziaFase(chiaveGiro, cronometro.FASI.ESTRAZIONE);
    try {
      let processedFile = null;

      // 🔍 Verifica cache prima di estrarre il testo
      if (userID && file.name && file.size) {
        console.log(`🔍 Avvio cache lookup per "${file.name}"...`);
        const cached = await findCachedDocument(userID, file.name, file.type, file.size);

        if (cached) {
          console.log(`📦 Cache HIT per "${file.name}" - uso testo cached`);
          processedFile = {
            name: file.name,
            type: file.type,
            fileSize: file.size,
            extractedText: cached.text,
            fromCache: true,
            cachedDocumentId: cached.documentId
          };
          cronometro.segnaStrada(chiaveGiro, cronometro.STRADE.CACHE);
        } else {
          console.log(`📭 Cache MISS per "${file.name}" - procedo con estrazione`);
        }
      } else {
        console.log(`⚠️ Cache skip: userID=${userID}, fileName=${file.name}, fileSize=${file.size}`);
      }

      // Se non in cache, procedi con estrazione normale
      if (!processedFile) {
        // L'avanzamento VERO dell'estrazione: prima `processFile` veniva
        // chiamata senza callback e la card mostrava una scritta fissa
        // mentre un PDF da 200 MB si leggeva in silenzio. Ogni evento va in
        // Redux (la card lo mostra) e al cronometro (che ne ricava le fasi).
        const onProgress = (avanzamento) => {
          try {
            cronometro.registraAvanzamento(chiaveGiro, avanzamento);
            store.dispatch(setExtractionProgress({
              fileName: file.name,
              phase: avanzamento?.fase || avanzamento?.phase || null,
              progress: avanzamento?.progress,
              message: avanzamento?.message,
            }));
          } catch (_) {
            // un progresso che non arriva non deve fermare l'estrazione
          }
        };
        processedFile = await processFile(file, onProgress);
        // Aggiungi size per future cache lookup
        if (processedFile) {
          processedFile.size = file.size;
          processedFile.fileSize = file.size;
        }
      }

      if (!processedFile) {
        store.dispatch(clearExtractionProgress());
        return;
      }

      // Check if processFile returned an error (not thrown)
      if (processedFile.errorMessage) {
        const motivo = motivoDaErrore(processedFile, file);
        cronometro.segnaFallimento(chiaveGiro, { fase: cronometro.FASI.ESTRAZIONE, errore: processedFile.errorMessage, motivo });
        // L'esito restituito (non lanciato) viene dal condotto largo, cioe' dai
        // file sopra i 20 MiB: i PDF da 41-54 MB caduti su Storage. Lo
        // studente lo sa dall'avviso. Il file pero' non entra nella coda: il
        // "si salta e si va avanti" di questo ramo e' un contratto che
        // uploadNotesAction.progresso.test.js fissa (STU-424, da decidere).
        avvisaLetturaFallita(file, motivo);
        // Estrai estensione dal nome file
        const fileExtension = file.name ? '.' + file.name.split('.').pop().toLowerCase() : 'unknown';

        // Determina il codice errore basato sul messaggio
        let errorCode = 'EXTRACTION_ERROR';
        const errorMsg = processedFile.errorMessage;
        if (errorMsg.includes('troppo grande') || errorMsg.includes('too large')) {
          errorCode = 'FILE_TOO_LARGE';
        } else if (errorMsg.includes('300 caratteri') || errorMsg.includes('più testo')) {
          errorCode = 'FILE_TOO_SMALL';
        } else if (errorMsg.includes('corrotto') || errorMsg.includes('corrupt') || errorMsg.includes('non valido')) {
          errorCode = 'CORRUPTED_FILE';
        } else if (errorMsg.includes('timeout') || errorMsg.includes('troppo tempo')) {
          errorCode = 'NETWORK_TIMEOUT';
        } else if (errorMsg.includes('Nessun testo estratto') || errorMsg.includes('estrazione')) {
          errorCode = 'EXTRACTION_ERROR';
        }

        console.error(`❌ File processing returned error for ${file.name}:`, errorMsg);

        // Track upload fallito
        try {
          trackUploadFailed({
            error_code: errorCode,
            error_message: errorMsg.substring(0, 200),
            file_extension: fileExtension,
            mime_type: file.type || 'unknown',
            file_size_mb: file.size ? parseFloat((file.size / (1024 * 1024)).toFixed(2)) : 0,
            upload_source: 'file_picker',
            extraction_method: 'processFile_return_error'
          });
        } catch (trackingError) {
          console.error("Errore nel tracciamento upload fallito:", trackingError);
        }

        // Skip this file but continue with others
        continue;
      }

      if (vaultPathsByFile) {
        processedFile.metadata = {
          ...processedFile.metadata,
          vaultRelativePath: vaultPathsByFile.get(file) ?? null,
        };
      }

      // Provenienza (es. Google Drive): la mette il connettore sull'oggetto
      // File, qui si copia nei metadati cosi' la card e il documento sanno
      // da dove arriva. Vedi services/googleDrive/driveDownload.js.
      const origin = originsByFile?.get(file) || file.origin || null;
      if (origin) {
        processedFile.metadata = { ...processedFile.metadata, origin };
      }

      // Testo pronto: da qui in poi il tempo e' dello studente, fino al
      // click su Genera.
      cronometro.testoPronto(chiaveGiro, processedFile.extractedText?.length || 0);
      promiseArray = [...promiseArray, processedFile];
    } catch (error) {
      console.error(`Error processing file ${file.name}:`, error);

      // Estrai estensione dal nome file
      const fileExtension = file.name ? '.' + file.name.split('.').pop().toLowerCase() : 'unknown';

      // Determina il codice errore basato sul messaggio
      let errorCode = 'UNKNOWN_ERROR';
      const errorMsg = error?.message || error?.errorMessage || '';
      // Il motivo, deciso una volta per tutti: avviso, card e GA4 (STU-424).
      const motivo = motivoDaErrore(error, file);
      cronometro.segnaFallimento(chiaveGiro, { fase: cronometro.FASI.ESTRAZIONE, errore: errorMsg, motivo });
      avvisaLetturaFallita(file, motivo);
      if (errorMsg.includes('troppo grande') || errorMsg.includes('too large')) {
        errorCode = 'FILE_TOO_LARGE';
      } else if (errorMsg.includes('300 caratteri') || errorMsg.includes('più testo')) {
        errorCode = 'FILE_TOO_SMALL';
      } else if (errorMsg.includes('corrotto') || errorMsg.includes('corrupt') || errorMsg.includes('non valido')) {
        errorCode = 'CORRUPTED_FILE';
      } else if (errorMsg.includes('timeout') || errorMsg.includes('troppo tempo')) {
        errorCode = 'NETWORK_TIMEOUT';
      } else if (errorMsg.includes('Nessun testo estratto') || errorMsg.includes('estrazione')) {
        errorCode = 'EXTRACTION_ERROR';
      } else if (errorMsg.includes('memoria') || errorMsg.includes('memory')) {
        errorCode = 'MEMORY_ERROR';
      }

      // Track upload fallito con dettagli completi
      try {
        trackUploadFailed({
          error_code: errorCode,
          error_message: errorMsg.substring(0, 200) || 'Unknown error',
          file_extension: fileExtension,
          mime_type: file.type || 'unknown',
          file_size_mb: file.size ? parseFloat((file.size / (1024 * 1024)).toFixed(2)) : 0,
          upload_source: 'file_picker'
        });
      } catch (trackingError) {
        console.error("Errore nel tracciamento upload fallito:", trackingError);
      }

      // Track legacy per compatibilità
      try {
        const userAuth = auth.currentUser;
        if (userAuth) {
          const userDoc = await getDoc(doc(firestore, "students", userAuth.uid));
          const userData = userDoc.exists() ? userDoc.data() : null;
          const isFreePlan = (userData?.plan || "Base") === "Base";

          if (isFreePlan) {
            trackEvent('file_processing_error', {
              category: 'error',
              error_type: 'file_processing',
              error_message: errorMsg.substring(0, 100) || 'Unknown error',
              file_name: file.name || 'unknown_file',
              file_type: file.type || 'unknown_type',
              file_size: file.size || 0
            });
          }
        }
      } catch (trackingError) {
        console.error("Errore nel tracciamento:", trackingError);
      }

      // Prima entrava l'esito grezzo, con Genera acceso: la card mostrava
      // "Nessun testo estratto" e il file partiva lo stesso verso l'AI.
      promiseArray = [...promiseArray, elementoFallito(file, error, motivo)];
    }
  }

  // La card torna alla scritta di riposo: l'ultimo evento di avanzamento
  // non deve restare a schermo dopo che il testo e' pronto.
  store.dispatch(clearExtractionProgress());

  return promiseArray;
};

// user profile action
export const userProfileAction = async ({ request }) => {
  const formData = await request.formData();
  const userInput = Object.fromEntries(formData);

  if (!auth.currentUser) {
    toast.error(t('toast.error.sessionExpiredRetry'));
    return null;
  }

  const normalizedFullName = userInput.fullName?.trim() || "";
  const normalizedEmail = userInput.email?.trim() || "";
  const normalizedStudyAt = userInput.studyAt ?? null;
  const normalizedGender = userInput.gender ?? null;

  const firebaseUser = auth.currentUser;
  const currentEmail = firebaseUser.email || "";
  let finalEmail = currentEmail;
  let emailError = null;

  try {
    if (normalizedFullName && normalizedFullName !== firebaseUser.displayName) {
      await updateProfile(firebaseUser, { displayName: normalizedFullName });
    }
  } catch (error) {
    console.error("Errore nell'aggiornamento del displayName:", error);
  }

  if (normalizedEmail && normalizedEmail !== currentEmail) {
    try {
      await updateEmail(firebaseUser, normalizedEmail);
      finalEmail = normalizedEmail;
    } catch (error) {
      emailError = error;

      if (error.code === "auth/requires-recent-login") {
        toast.error(t('toast.error.emailChangeReauth'));
      } else if (error.code === "auth/email-already-in-use") {
        toast.error(t('toast.error.emailAlreadyUsed'));
      } else if (error.code === "auth/invalid-email") {
        toast.error(t('toast.error.invalidEmail'));
      } else {
        toast.error(error.message || t('toast.error.emailUpdateFailed'));
      }
    }
  }

  const resourceRef = doc(firestore, "students", firebaseUser.uid);
  const updatePayload = {
    fullName: normalizedFullName,
    email: finalEmail,
  };

  if (normalizedStudyAt !== null) {
    updatePayload.studyAt = normalizedStudyAt;
    // Sincronizza educationStage con studyAt per evitare disallineamenti
    // (educationStage viene settato in onboarding e mai aggiornato)
    const studyAtToStageMap = { 'Medie': 'medie', 'Superiori': 'liceo', 'Università': 'università' };
    if (studyAtToStageMap[normalizedStudyAt]) {
      updatePayload.educationStage = studyAtToStageMap[normalizedStudyAt];
    }
  }

  if (normalizedGender !== null) {
    updatePayload.gender = normalizedGender;
  }

  try {
    await updateDoc(resourceRef, updatePayload);
  } catch (error) {
    console.error("Errore aggiornando il documento studente:", error);
    toast.error(t('toast.error.profileSaveFailed'));
    return null;
  }

  if (emailError) {
    toast.info(t('toast.success.profilePartialUpdate'));
  } else {
    toast.success(t('toast.success.profileUpdated'));
  }

  return {
    user: {
      ...userInput,
      fullName: normalizedFullName,
      email: finalEmail,
      studyAt: normalizedStudyAt,
      gender: normalizedGender,
    },
  };
};

// get feedback from chat gpt
export const getFeedbackQuestionAction = async ({ request }) => {
  let data = {};
  try {
    const formData = await request.formData();
    data = Object.fromEntries(formData);

    console.log('[Feedback] Dati ricevuti:', { messageID: data.messageID, contentUUID: data.contentUUID, hasQuestion: !!data.question, hasCorrectAnswer: !!data.correctAnswer, detectedLanguage: data.detectedLanguage, documentLanguage: data.documentLanguage });

    if (data.question && data.correctAnswer) {
      console.log('[Feedback] Usando domanda fornita direttamente (skip Firestore query)');

      const feedbackData = await getFeedback(
        data.answer,
        data.question,
        data.correctAnswer,
        data.detectedLanguage || null,
        data.documentLanguage || null,
        data.filterTopic || null,
        data.feedbackLanguage || null
      );

      // Valida la struttura della risposta feedback
      validateFeedbackResponse(feedbackData, '/get-feedback');

      return {
        feedback: feedbackData.feedback,
        score: feedbackData.score,
        questionType: 'exam'
      };
    }

    // Fallback: search question on firebase (legacy behavior)
    console.log('[Feedback] Ricerca domanda in Firestore:', { messageID: data.messageID, contentUUID: data.contentUUID });
    let questionEntry = {};

    // ✅ STRATEGIA MULTI-FALLBACK PER NUOVO FORMATO V2 (qaMap)

    // Tentativo 1: Usa contentUUID per attivare il fallback intelligente in getQuestion
    // Questo funziona con il nuovo formato v2 (qaMap)
    if (data.contentUUID && data.messageID) {
      console.log('[Feedback] Tentativo 1: Ricerca con contentUUID (formato v2 qaMap)');
      try {
        questionEntry = await getQuestion(null, data.messageID, data.contentUUID);
        if (questionEntry && Object.keys(questionEntry).length > 0) {
          console.log('[Feedback] ✅ Domanda trovata con contentUUID fallback');
        }
      } catch (e) {
        console.log('[Feedback] Tentativo 1 fallito:', e.message);
      }
    }

    // Tentativo 2: Parsing del messageID per formato legacy
    if (!questionEntry || Object.keys(questionEntry).length === 0) {
      console.log('[Feedback] Tentativo 2: Parsing messageID per formato legacy');

      const parts = data.messageID.split('_');

      // Prova diverse combinazioni di parsing
      // Formato possibili:
      // - "qa_1_abc123" → documentId potrebbe essere combinazione
      // - "exam_abc123_456" → documentId è abc123
      // - "qa_abc123" → documentId è abc123

      if (parts.length >= 2) {
        // Prova con il secondo elemento come documentId
        const potentialDocId = parts[1];

        try {
          questionEntry = await getQuestion(potentialDocId, data.messageID, data.contentUUID);
          if (questionEntry && Object.keys(questionEntry).length > 0) {
            console.log('[Feedback] ✅ Domanda trovata con documentId parsed:', potentialDocId);
          }
        } catch (e) {
          console.log('[Feedback] Tentativo 2 fallito con documentId:', potentialDocId);
        }
      }
    }

    // Tentativo 3: Prova con messageID completo come documentId (formato legacy diretto)
    if (!questionEntry || Object.keys(questionEntry).length === 0) {
      console.log('[Feedback] Tentativo 3: messageID come documentId diretto (legacy)');
      try {
        questionEntry = await getQuestion(data.messageID, data.messageID, data.contentUUID);
        if (questionEntry && Object.keys(questionEntry).length > 0) {
          console.log('[Feedback] ✅ Domanda trovata con messageID come documentId');
        }
      } catch (e) {
        console.log('[Feedback] Tentativo 3 fallito');
      }
    }

    // Verifica finale
    if (!questionEntry || Object.keys(questionEntry).length === 0) {
      console.error('[Feedback] ❌ Domanda non trovata dopo tutti i tentativi', {
        messageID: data.messageID,
        contentUUID: data.contentUUID
      });
      throw new QuestionsNotFoundError(data.messageID, {
        contentUUID: data.contentUUID,
        attempts: 3
      });
    }

    console.log('[Feedback] ✅ Domanda recuperata, richiesta feedback a OpenAI');

    let feedbackData = '';

    // Passa la lingua rilevata anche nel fallback legacy
    feedbackData = await getFeedback(
      data.answer,
      questionEntry.question,
      questionEntry.answer,
      data.detectedLanguage || null,
      null,  // documentLanguage
      data.filterTopic || null,
      // Anche qui la lingua del tutor (STU-529), come nella strada principale.
      data.feedbackLanguage || null
    );

    // Valida la struttura della risposta feedback
    validateFeedbackResponse(feedbackData, '/get-feedback');

    return {
      feedback: feedbackData.feedback,
      score: feedbackData.score,
      questionType: questionEntry.type
    };
  } catch (error) {
    console.error('[Feedback] Errore:', error);

    // Report a Sentry con contesto workflow
    sendErrorToSentry(error, {
      workflow: 'getFeedbackQuestion',
      messageID: data.messageID,
      contentUUID: data.contentUUID
    });

    // Usa userMessage dall'AppError se disponibile, altrimenti fallback
    const userMessage = error.userMessage || 'Si è verificato un errore nel recupero del feedback. Riprova.';
    toast.error(userMessage);

    return { error: userMessage, errorCode: error.code || 'UNKNOWN' };
  }
};
